import type { FC } from 'react';

import { useApi } from '@backstage/frontend-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { isLeanixEntity } from '@ipen-portal/plugin-common';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { skipToken, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { tss } from 'tss-react/mui';

import { tdhApiRef } from '../apis';
import { DataRow } from './DataRow';
import { TdhDetailTile } from './TdhDetailTile';

const useStyles = tss.create(({ theme }) => ({
  alert: {
    marginTop: theme.spacing(1),
  },
  stack: {
    marginTop: theme.spacing(1),
  },
}));

export const GdprAiGovernanceCard: FC = () => {
  const tdhApi = useApi(tdhApiRef);
  const { entity } = useEntity();
  const leanixId = isLeanixEntity(entity)
    ? entity.spec.leanix.aplElementId
    : undefined;
  const factsheetUrl = isLeanixEntity(entity)
    ? entity.spec.leanix.factsheetUrl
    : undefined;
  const { data, error, isLoading } = useQuery(
    tdhApi.getGdprAndAiGovernance.queryOptions(
      leanixId ? { aplElementId: leanixId } : skipToken,
    ),
  );
  const { classes } = useStyles();

  const content = data
    ? (
        <Stack className={classes.stack} spacing={2}>
          <Box>
            <Typography gutterBottom variant="h6">
              Data Processing
            </Typography>
            <Stack spacing={1}>
              <DataRow
                label="Categories of Personal Data:"
                value={data.dataProcessing.categoriesOfPersonalData ?? 'Unknown'}
              />
              <DataRow
                label="Confirmation of Data Protection by Design and Default:"
                value={data.dataProcessing.dpbdConfirmation ?? 'Unknown'}
              />
            </Stack>
          </Box>

          <Box>
            <Typography gutterBottom variant="h6">
              Deletion Concept
            </Typography>
            <Stack spacing={1}>
              <DataRow
                label="Deletion Concept Status:"
                value={data.deletionConcept.status ?? 'Unknown'}
              />
            </Stack>
          </Box>

          <Box>
            <Typography gutterBottom variant="h6">
              AI Governance and Adoption
            </Typography>
            <Stack spacing={1}>
              <DataRow
                label="AI Usage:"
                value={data.aiGovernanceAndAdoption.aiUsage ?? 'Unknown'}
              />
              <DataRow
                label="AI Governance - Review Date:"
                value={
                  data.aiGovernanceAndAdoption.aiGovernanceReviewDate
                    ? dayjs(
                        data.aiGovernanceAndAdoption.aiGovernanceReviewDate,
                      ).format('YYYY-MM-DD')
                    : '-'
                }
              />
            </Stack>
          </Box>
        </Stack>
      )
    : (
        <Alert className={classes.alert} severity="info">
          No data returned for this LeanIX ID
        </Alert>
      );

  return (
    <TdhDetailTile
      error={error}
      factsheetUrl={factsheetUrl}
      icon="mdi:shield-check"
      isLoading={isLoading}
      leanixId={leanixId}
      title="GDPR and AI Governance"
    >
      {content}
    </TdhDetailTile>
  );
};
